import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ArrowRight,
  CheckCircle2,
  DollarSign,
  XCircle,
  RotateCcw,
} from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { RefundSettlement } from '@/types/financial.types';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { SettlementTypeBadge } from '@/components/features/financials/shared/SettlementTypeBadge';
import { useI18n } from '@/contexts/I18nContext';

const STATUS_COLOR: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground border-border',
  approved: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  paid: 'bg-green-500/10 text-green-600 border-green-500/20',
  rejected: 'bg-red-500/10 text-red-600 border-red-500/20',
  reversed: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
};

const DEBTOR_LABEL: Record<string, string> = {
  carrier: 'شركة شحن',
  tenant: 'تاجر',
  platform: 'المنصة',
};

export default function RefundDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { get } = useApi();
  const { patch, loading } = useApi({ showSuccessToast: false, showErrorToast: false });
  const [refund, setRefund] = useState<RefundSettlement | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const STATUS_LABEL: Record<string, string> = {
    draft: t.financials.status_draft,
    approved: t.financials.status_approved,
    paid: t.financials.status_paid,
    rejected: t.financials.status_rejected,
    reversed: t.financials.status_reversed,
  };

  useEffect(() => {
    if (id) fetchRefund();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchRefund = async () => {
    setHasFetched(false);
    try {
      const result: any = await get(`/api/v1/refunds/${id}`);
      const data = result?.success !== undefined ? result?.data : result;
      setRefund(data);
    } catch {
      toast.error('فشل في تحميل تفاصيل المرتجع');
    } finally {
      setHasFetched(true);
    }
  };

  const handleApprove = async () => {
    try {
      await patch(`/api/v1/refunds/${id}/approve`, {});
      toast.success(t.financials.refund_approved);
      fetchRefund();
    } catch (err: any) {
      toast.error(err?.message ?? 'فشل في اعتماد المرتجع');
    }
  };

  const handleMarkPaid = async () => {
    const paid_via = window.prompt(t.financials.paid_via, 'bank_transfer');
    if (!paid_via) return;
    try {
      await patch(`/api/v1/refunds/${id}/pay`, { paid_via });
      toast.success(t.financials.refund_paid);
      fetchRefund();
    } catch (err: any) {
      toast.error(err?.message ?? 'فشل في تحديث حالة المرتجع');
    }
  };

  const handleReject = async () => {
    const reason = window.prompt(t.financials.refund_reason, '');
    if (!reason) return;
    try {
      await patch(`/api/v1/refunds/${id}/reject`, { reason });
      toast.success(t.financials.refund_rejected);
      fetchRefund();
    } catch (err: any) {
      toast.error(err?.message ?? 'فشل في رفض المرتجع');
    }
  };

  if (!hasFetched) return <LoadingSkeleton type="card" rows={4} />;
  if (!refund) return null;

  const customer = refund.customer_metadata ?? {};

  return (
    <PageContainer>
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
              تفاصيل المرتجع
              <SettlementTypeBadge type="refund" />
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {DEBTOR_LABEL[refund.debtor_type] ?? refund.debtor_type} —{' '}
              <span className="font-mono" dir="ltr">{refund.debtor_id}</span>
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/dashboard/refunds')}
            className="gap-1 self-start sm:self-auto"
          >
            <ArrowRight className="h-4 w-4" />
            رجوع
          </Button>
        </div>
      </div>

      <div className="space-y-6" dir="rtl">
        {/* Summary */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">معلومات المرتجع</CardTitle>
              <div className="flex items-center gap-2">
                <SettlementTypeBadge type="refund" />
                <Badge className={cn('border text-xs', STATUS_COLOR[refund.status])}>
                  {STATUS_LABEL[refund.status] ?? refund.status}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">{t.financials.amount}:</span>
                <span className="me-2 font-bold text-lg text-primary">
                  {formatCurrency(refund.amount)}
                </span>
              </div>

              <div>
                <span className="text-muted-foreground">تاريخ الإنشاء:</span>
                <span className="me-2">{formatDate(refund.created_at)}</span>
              </div>
              {refund.shipment_id && (
                <div>
                  <span className="text-muted-foreground">الشحنة:</span>
                  <button
                    onClick={() => navigate(`/dashboard/shipments/${refund.shipment_id}`)}
                    className="me-2 font-mono text-primary hover:underline"
                    dir="ltr"
                  >
                    {refund.shipment_id}
                  </button>
                </div>
              )}
              {refund.approved_at && (
                <div>
                  <span className="text-muted-foreground">تاريخ الاعتماد:</span>
                  <span className="me-2">{formatDate(refund.approved_at)}</span>
                </div>
              )}
              {refund.approved_by && (
                <div>
                  <span className="text-muted-foreground">تم الاعتماد بواسطة:</span>
                  <span className="me-2 font-mono" dir="ltr">{refund.approved_by}</span>
                </div>
              )}
              {refund.paid_at && (
                <div>
                  <span className="text-muted-foreground">تاريخ الدفع:</span>
                  <span className="me-2">{formatDate(refund.paid_at)}</span>
                </div>
              )}
              {refund.paid_via && (
                <div>
                  <span className="text-muted-foreground">{t.financials.paid_via}:</span>
                  <span className="me-2">{refund.paid_via}</span>
                </div>
              )}
              <div className="col-span-1 sm:col-span-2">
                <span className="text-muted-foreground">{t.financials.refund_reason}:</span>
                <p className="mt-1 whitespace-pre-wrap">{refund.reason}</p>
              </div>
              {refund.notes && (
                <div className="col-span-1 sm:col-span-2">
                  <span className="text-muted-foreground">ملاحظات:</span>
                  <p className="mt-1 whitespace-pre-wrap">{refund.notes}</p>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 pt-4 mt-4 border-t">
              {refund.status === 'draft' && (
                <>
                  <Button onClick={handleApprove} disabled={loading} className="gap-1.5">
                    <CheckCircle2 className="h-4 w-4" />
                    {t.financials.approve_refund}
                  </Button>
                  <Button
                    onClick={handleReject}
                    disabled={loading}
                    variant="outline"
                    className="gap-1.5 border-red-500 text-red-600 hover:bg-red-50"
                  >
                    <XCircle className="h-4 w-4" />
                    {t.financials.reject_refund}
                  </Button>
                </>
              )}
              {refund.status === 'approved' && (
                <Button
                  onClick={handleMarkPaid}
                  disabled={loading}
                  className="gap-1.5 bg-green-600 hover:bg-green-700"
                >
                  <DollarSign className="h-4 w-4" />
                  {t.financials.mark_paid}
                </Button>
              )}
              {(refund.status === 'rejected' || refund.status === 'reversed') && (
                <div className="text-sm text-muted-foreground flex items-center gap-1">
                  <RotateCcw className="h-4 w-4" />
                  لا توجد إجراءات متاحة
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Customer metadata */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">معلومات العميل</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              {customer.name && (
                <div>
                  <span className="text-muted-foreground">{t.financials.customer_name}:</span>
                  <span className="me-2 font-medium">{customer.name}</span>
                </div>
              )}
              {customer.phone && (
                <div>
                  <span className="text-muted-foreground">{t.financials.customer_phone}:</span>
                  <a
                    href={`tel:${customer.phone}`}
                    className="me-2 font-medium text-primary hover:underline"
                    dir="ltr"
                  >
                    {customer.phone}
                  </a>
                </div>
              )}
              {customer.iban && (
                <div className="col-span-1 sm:col-span-2">
                  <span className="text-muted-foreground">{t.financials.customer_iban}:</span>
                  <span className="me-2 font-mono" dir="ltr">{customer.iban}</span>
                </div>
              )}
              {Object.keys(customer)
                .filter((k) => !['name', 'phone', 'iban'].includes(k))
                .map((k) => (
                  <div key={k}>
                    <span className="text-muted-foreground">{k}:</span>
                    <span className="me-2">{String(customer[k])}</span>
                  </div>
                ))}
              {Object.keys(customer).length === 0 && (
                <div className="col-span-1 sm:col-span-2 text-sm text-muted-foreground">
                  لا توجد معلومات إضافية للعميل
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
